import { asapScheduler, forkJoin, Observable, scheduled } from 'rxjs';
import { mergeAll } from 'rxjs/operators';
import { getClientConfig } from 'src/clients-internal/configuration.init';
import { EntityRequest } from '../../../models/api/request.model';
import { ApiResponse } from '../../../models/api/response.model';
import { ObsApiExpected } from '../../../models/api/service.model';
import { AttachmentCreate } from '../../../models/attachments/attachment-create.model';
import { fetchHelper } from '../../helpers/fetch.helper';
import { CareerHubVersion } from '../api-configuration/api-configuration.reducer';

type AttachmentEntityType = 'jobs' | 'eventrequests' | 'events'; // used in route (hacky)

export interface AttachmentEntity {
    type: AttachmentEntityType;
    id: number;
}

const is5_17 = (version: CareerHubVersion) =>
    getClientConfig().disableVersionChecks ||
    version.major > 5 ||
    (version.major === 5 && version.minor >= 17);

const getServicePath = () => `${getClientConfig().careerHubBasePath}/services/employers/v1`;

const getPostPath = (version: CareerHubVersion, entity: AttachmentEntity) =>
    is5_17(version)
        ? `${getServicePath()}/${entity.type}/${entity.id}/attachments`
        : `${getServicePath()}/attachments/job/${entity.id}`;

export const getDelOrBlobPath = (
    version: CareerHubVersion,
    entity: AttachmentEntity,
    storedFileId: number
) =>
    is5_17(version)
        ? `${getServicePath()}/${entity.type}/${entity.id}/attachments/${storedFileId}`
        : `${getServicePath()}/attachments/${storedFileId}`;

export function add(
    version: CareerHubVersion,
    entity: AttachmentEntity,
    files: AttachmentCreate[]
): Observable<unknown> {
    const requests$ = files.map(file => {
        const formData = new FormData();
        formData.append('title', file.title);
        formData.append('file', file.file);

        return fetchHelper.fetch(getPostPath(version, entity), 'POST', formData);
    });

    return forkJoin([scheduled(requests$, asapScheduler).pipe(mergeAll(3))]);
}

export function deleteStoredFile(
    version: CareerHubVersion,
    entity: AttachmentEntity,
    storedFileId: number
): ObsApiExpected<ApiResponse<EntityRequest>> {
    return fetchHelper.del(getDelOrBlobPath(version, entity, storedFileId));
}

export function downloadAsBlob(
    version: CareerHubVersion,
    entity: AttachmentEntity,
    storedFileId: number
): ObsApiExpected<unknown> {
    return fetchHelper.get(getDelOrBlobPath(version, entity, storedFileId), {
        expectBlobResponse: true,
        credentials: 'include',
    });
}
